// The side menu and the header's second-level menu (e.g. Reports / Saved
// reports / Certificate downloads) follow the page being shown. The stored
// navigation.parent and navigation.child only change on menu clicks, so a
// link that opens another section's page (such as "Open" on a saved report,
// or a download link from an email) left the old items highlighted, and
// clicking the highlighted item then did nothing.

const onPath = (path, pathname) =>
  !!path && (pathname === path || pathname.startsWith(`${path}/`));

// True when pathname is the menu's own page or a page below it. A click on a
// menu navigates unless this is true (activePaths do not count, so a download
// page can still go back to its section's list).
export const isOnMenu = (menu, pathname) => onPath(menu?.to, pathname);

// The length of the longest of the menu's paths (its own and its
// activePaths: pages elsewhere that belong to it) holding pathname, or -1.
const matchLength = (menu, pathname) =>
  [menu?.to, ...(menu?.activePaths || [])]
    .filter((path) => onPath(path, pathname))
    .reduce((longest, path) => Math.max(longest, path.length), -1);

// The id of the item that holds pathname best, or fallback when none does.
const bestMatch = (items, pathname, score, fallback) => {
  let best = null;
  let bestLength = -1;
  for (const item of items || []) {
    const length = score(item, pathname);
    if (length > bestLength) {
      best = item;
      bestLength = length;
    }
  }
  return best ? best.id : fallback;
};

// The parent's child menu that holds pathname, or fallback.
export const activeChildMenu = (parent, pathname, fallback) =>
  bestMatch(parent?.childrens, pathname, matchLength, fallback);

// The top-level menu whose own paths or whose children's paths hold
// pathname, or fallback.
export const activeParentMenu = (menus, pathname, fallback) =>
  bestMatch(
    menus,
    pathname,
    (menu) =>
      Math.max(
        matchLength(menu, pathname),
        ...(menu.childrens || []).map((child) => matchLength(child, pathname))
      ),
    fallback
  );
